import React, { useEffect, useRef, useState } from 'react'
import { groupBy } from 'lodash'
import { useParams } from 'react-router-dom'
import { graphql, useLazyLoadQuery } from 'react-relay/hooks'
import { Box, Text, VerticalTabBar, FlexColumn, FlexRow, Button, ProgressBar } from 'elements'
import { useAnalytics } from 'hooks'
import useWindowSize from '../../../hooks/ui/useWindowSize'

export default function ReportMenu({ options, value, onChange }) {
  const analytics = useAnalytics()
  const ref = useRef()
  const [height, setHeight] = useState(0)
  const { documentHeight } = useWindowSize()
  const { reportId } = useParams()

  const data = useLazyLoadQuery(
    graphql`
      query ReportMenuQuery($reportId: ID!) {
        reportById(id: $reportId) {
          resolutionProgress
        }
      }
    `,
    { reportId },
  )

  useEffect(() => {
    const newHeight = documentHeight - ref.current.getBoundingClientRect().top

    setHeight(newHeight)
  }, [ref, documentHeight])

  const handleTabChange = (option) => {
    onChange(option)
    analytics.track('Anomaly View Changed', { anomaly: option.label })
  }

  const groupedOptions = groupBy(options, 'kind')

  const resolutionOption = groupedOptions.resolution?.[0]
  const resolutionProgress = data ? Math.floor(data.reportById.resolutionProgress * 100) : 0

  return (
    <Box
      ref={ref}
      zIndex={2}
      position="absolute"
      top="100%"
      left="0"
      py="xlarge"
      pl="xlarge"
      pr="medium"
      bg="background.0"
      overflow="auto"
      width="300px"
      height={`${height}px`}
      // same color border as page background for horizontal scroll tables
      borderRight="solid 2px"
      borderColor="background.1"
    >
      {resolutionOption && (
        <FlexColumn alignItems="flex-start" mb="xsmall">
          <Text
            color={
              resolutionOption && resolutionOption.value === value
                ? 'accent.lightblue'
                : 'neutral.3'
            }
            fontSize="large"
            fontWeight="bold"
            mb="xlarge"
          >
            ANOMALY RESOLUTION
          </Text>
          <FlexRow width="90%" alignItems="center" mb="xlarge">
            <FlexColumn width="100%" mr="medium">
              <ProgressBar
                progress={resolutionProgress}
                styleProps={{
                  backgroundColor: 'secondary.3',
                  height: 'small',
                  borderRadius: 'medium',
                }}
              />
            </FlexColumn>
            <Text>{resolutionProgress}%</Text>
          </FlexRow>
          <Button
            kind="alt-secondary"
            onClick={() => handleTabChange(resolutionOption)}
            width="90%"
            mb="xlarge"
          >
            View Anomalies
          </Button>
        </FlexColumn>
      )}
      {groupedOptions.insight && (
        <>
          <Text
            fontSize="large"
            lineHeight="large"
            fontWeight="bold"
            textTransform="uppercase"
            pl="small"
            mb="small"
            color="neutral.3"
          >
            Insights
          </Text>
          <VerticalTabBar tabs={groupedOptions.insight} value={value} onChange={handleTabChange} />
        </>
      )}
      {groupedOptions.report && (
        <>
          <Text
            fontSize="large"
            lineHeight="large"
            fontWeight="bold"
            textTransform="uppercase"
            pl="small"
            mt="large"
            pt="xsmall"
            mb="small"
            color="neutral.3"
          >
            Reports
          </Text>
          <VerticalTabBar tabs={groupedOptions.report} value={value} onChange={handleTabChange} />
        </>
      )}
    </Box>
  )
}
